import { motion } from "framer-motion";
import { Link } from "wouter";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import CertificatePDFViewer from "./CertificatePDFViewer";
import { CERTIFICATION_DESIGN } from "@/lib/utils";
import { encodeSkillName } from "@/lib/skills/core/skillUtils";
import { 
  Calendar, 
  Building, 
  Shield, 
  CheckCircle,
  AlertCircle,
  Clock,
  Award,
  ExternalLink
} from "lucide-react";

interface CertificateDetailsProps {
  certificate: {
    id: number;
    title: string;
    issuingOrganization: string;
    issueDate: string;
    expiryDate?: string | null;
    pdfPath?: string | null;
    skills?: string[] | null;
    logoPath?: string | null;
    link?: string | null;
  };
}

export default function CertificateDetails({ certificate }: CertificateDetailsProps) {
  const isExpired = certificate.expiryDate ? new Date(certificate.expiryDate) < new Date() : false;
  const isExpiringSoon = certificate.expiryDate ? 
    new Date(certificate.expiryDate) < new Date(Date.now() + 90 * 24 * 60 * 60 * 1000) && !isExpired : false;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusInfo = () => {
    if (isExpired) {
      return {
        icon: AlertCircle,
        text: "Expired",
        variant: "destructive" as const,
        color: "text-red-500"
      };
    }
    if (isExpiringSoon) {
      return {
        icon: Clock,
        text: "Expiring Soon",
        variant: "outline" as const,
        color: "text-orange-500"
      };
    }
    return {
      icon: CheckCircle,
      text: certificate.expiryDate ? "Active" : "No Expiration",
      variant: "default" as const,
      color: "text-green-500"
    };
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Header Section */}
      <div className="relative pl-4">
        <div className="inline-block px-4 py-2 mb-4 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/50 via-primary/20 to-transparent rounded-lg blur-lg"></div>
          <h1 className="text-3xl font-bold relative z-10">{certificate.title}</h1>
        </div>
      </div>

      {/* Top section with certificate overview on left and certificate preview on right */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Left side: Certificate Overview */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <CardTitle className="flex items-center gap-2">
                <Award className={`h-5 w-5 ${CERTIFICATION_DESIGN.colors.badge}`} />
                Certificate Overview
              </CardTitle>
              <div className="flex items-center gap-2">
                <StatusIcon className={`h-4 w-4 ${statusInfo.color}`} />
                <Badge variant={statusInfo.variant}>
                  {statusInfo.text}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Issuing Organization */}
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <Building className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Issuing Organization</p>
                <p className="font-medium">{certificate.issuingOrganization}</p>
              </div>
            </div>

            {/* Dates */}
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Issue Date</p>
                  <p className="font-medium">{formatDate(certificate.issueDate)}</p>
                </div>
              </div>
              
              {certificate.expiryDate ? (
                <div className={`flex items-center gap-3 p-3 rounded-lg ${
                  isExpired ? 'bg-red-50 border border-red-200' : 
                  isExpiringSoon ? 'bg-orange-50 border border-orange-200' : 
                  'bg-muted/50'
                }`}>
                  <Calendar className={`h-5 w-5 ${
                    isExpired ? 'text-red-500' : 
                    isExpiringSoon ? 'text-orange-500' : 
                    'text-muted-foreground'
                  }`} />
                  <div>
                    <p className="text-sm text-muted-foreground">Expiration Date</p>
                    <p className={`font-medium ${
                      isExpired ? 'text-red-600' : 
                      isExpiringSoon ? 'text-orange-600' : ''
                    }`}>
                      {formatDate(certificate.expiryDate)}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Validity</p>
                    <p className="font-medium text-green-600">No Expiration</p>
                  </div>
                </div>
              )}
            </div>

            {/* Skills */}
            {certificate.skills && certificate.skills.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Skills Covered</p>
                <div className="flex flex-wrap gap-2">
                  {certificate.skills.map((skill, index) => (
                    <Link key={index} href={`/professional/skill/${encodeSkillName(skill)}`}>
                      <Badge variant="secondary" className="text-sm cursor-pointer hover:bg-secondary/80 transition-colors">
                        {skill}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Verification Link */}
            {certificate.link && (
              <div className="pt-2">
                <Button 
                  asChild 
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-white"
                  variant="default"
                >
                  <a 
                    href={certificate.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2"
                  >
                    <Shield className="h-4 w-4" />
                    Verify Certificate
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right side: Certificate Preview */}
        <div>
          {certificate.pdfPath && (
            <CertificatePDFViewer 
              pdfUrl={certificate.pdfPath} 
              title="Certificate Preview"
              className="h-fit"
            />
          )}
        </div>
      </div>

    </motion.div>
  );
}
